/* eslint-disable react-hooks/exhaustive-deps */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Activity,
  Search,
  ShieldCheck,
  Clock,
  Flame,
  Sparkles,
  ExternalLink,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
} from "lucide-react";

const STATUS_META = {
  red:   { color: "#DC2626", label: "Critical",  order: 0 },
  amber: { color: "#D97706", label: "Attention", order: 1 },
  green: { color: "#16A34A", label: "Cleared",   order: 2 },
  grey:  { color: "#64748B", label: "Idle",      order: 3 },
};

const FILTERS = [
  { key: "all",       label: "All",           test: () => true },
  { key: "critical",  label: "Critical",      test: (d) => d.critical_open > 0 },
  { key: "attention", label: "Attention",     test: (d) => d.status === "amber" },
  { key: "certified", label: "Certified",     test: (d) => d.ready },
  { key: "pending",   label: "Cert Pending",  test: (d) => !d.ready },
];

const COLUMNS = [
  { key: "status",            label: "Status",         sortable: true, className: "w-24" },
  { key: "department",        label: "Department",     sortable: true },
  { key: "total_tasks",       label: "Total Tasks",    sortable: true, align: "text-center", className: "w-24" },
  { key: "open_tasks",        label: "Open",           sortable: true, align: "text-center", className: "w-20" },
  { key: "pending_count",     label: "Pending",        sortable: true, align: "text-center", className: "w-20" },
  { key: "in_progress_count", label: "In Progress",    sortable: true, align: "text-center", className: "w-24" },
  { key: "critical_open",     label: "Critical",       sortable: true, align: "text-center", className: "w-24" },
  { key: "ready",             label: "Match Day Cert", sortable: true, className: "w-44" },
  { key: "actions",           label: "", align: "text-right", className: "w-36" },
];

function StatusPill({ status }) {
  const meta = STATUS_META[status] || STATUS_META.grey;
  return (
    <span
      className={`inline-flex items-center gap-1.5 border px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold`}
      style={{ color: meta.color, borderColor: meta.color, backgroundColor: `${meta.color}12` }}
    >
      <span
        className={`inline-block w-1.5 h-1.5 rounded-full ${status !== "green" ? "animate-pulse" : ""}`}
        style={{ backgroundColor: meta.color }}
      />
      {meta.label}
    </span>
  );
}

function CountCell({ value, tone }) {
  const toneCls =
    tone === "red"   ? "text-incidentRed" :
    tone === "amber" ? "text-warnAmber" :
    tone === "green" ? "text-readyGreen" :
                       "text-zinc-500";
  return (
    <span className={`font-mono-live font-bold text-base ${toneCls}`}>{value}</span>
  );
}

function CertBadge({ ready, submittedBy }) {
  if (ready) {
    return (
      <div className="flex items-center gap-2">
        <span className="inline-flex items-center gap-1.5 border border-readyGreen/50 text-readyGreen bg-readyGreen/10 px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold">
          <ShieldCheck className="w-3 h-3" /> Ready
        </span>
        {submittedBy && (
          <span className="text-[10px] text-zinc-500 truncate" title={`Submitted by ${submittedBy}`}>
            {submittedBy}
          </span>
        )}
      </div>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 border border-warnAmber/50 text-warnAmber bg-warnAmber/10 px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold">
      <Clock className="w-3 h-3" /> Pending
    </span>
  );
}

function DeptCardMobile({ d, code }) {
  const meta = STATUS_META[d.status] || STATUS_META.grey;
  const openTone = d.open_tasks === 0 ? "grey" : d.critical_open > 0 ? "red" : "amber";
  const critTone = d.critical_open > 0 ? "red" : "grey";
  return (
    <div className="mc-panel p-3 relative overflow-hidden" data-testid={`gcg-mobile-${d.department}`}>
      <span className="absolute left-0 top-0 bottom-0 w-1" style={{ backgroundColor: meta.color }} />
      <div className="pl-3">
        <Link
          to={`/pre-match/tracker?dept=${encodeURIComponent(d.department)}`}
          className="flex items-center justify-between gap-2"
          data-testid={`gcg-mobile-open-${d.department}`}
        >
          <div className="flex items-center gap-2 min-w-0">
            <span className="font-mono-live text-[10px] text-zinc-500">{code}</span>
            <span className="font-semibold text-sm truncate">{d.department}</span>
          </div>
          <StatusPill status={d.status} />
        </Link>
        <div className="grid grid-cols-3 gap-2 mt-3 text-[11px]">
          <div className="flex flex-col items-center border border-cmdBorder p-2">
            <span className="text-zinc-500 uppercase tracking-widest text-[9px]">Total</span>
            <CountCell value={d.total_tasks ?? 0} tone="grey" />
          </div>
          <div className="flex flex-col items-center border border-cmdBorder p-2">
            <span className="text-zinc-500 uppercase tracking-widest text-[9px]">Open</span>
            <CountCell value={d.open_tasks} tone={openTone} />
          </div>
          <div className="flex flex-col items-center border border-cmdBorder p-2">
            <span className="text-zinc-500 uppercase tracking-widest text-[9px]">Critical</span>
            <CountCell value={d.critical_open} tone={critTone} />
          </div>
          <div className="flex flex-col items-center border border-cmdBorder p-2">
            <span className="text-zinc-500 uppercase tracking-widest text-[9px]">Pending</span>
            <CountCell value={d.pending_count ?? 0} tone={d.pending_count > 0 ? "amber" : "grey"} />
          </div>
          <div className="flex flex-col items-center border border-cmdBorder p-2 col-span-2">
            <span className="text-zinc-500 uppercase tracking-widest text-[9px]">In-Progress</span>
            <CountCell value={d.in_progress_count ?? 0} tone={d.in_progress_count > 0 ? "amber" : "grey"} />
          </div>
        </div>
        <div className="mt-3">
          <CertBadge ready={d.ready} submittedBy={d.submitted_by} />
        </div>
        <div className="mt-3 flex items-center gap-2">
          <Link to={`/pre-match/tracker?dept=${encodeURIComponent(d.department)}`} className="flex-1 text-center border border-cmdBorder hover:border-white text-[10px] uppercase tracking-widest px-2 py-2 flex items-center justify-center gap-1">
            Tracker <ExternalLink className="w-3 h-3" />
          </Link>
          <Link to="/readiness" className="flex-1 text-center border border-cmdBorder hover:border-white text-[10px] uppercase tracking-widest px-2 py-2 flex items-center justify-center gap-1">
            Cert <ExternalLink className="w-3 h-3" />
          </Link>
        </div>
      </div>
    </div>
  );
}

export default function GroundCommandGrid({ departments = [] }) {
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [sortKey, setSortKey] = useState("status");
  const [sortDir, setSortDir] = useState("asc");

  const counts = useMemo(() => {
    const c = { red: 0, amber: 0, green: 0, grey: 0, critical: 0, certified: 0 };
    for (const d of departments) {
      c[d.status] = (c[d.status] || 0) + 1;
      if (d.critical_open > 0) c.critical += 1;
      if (d.ready) c.certified += 1;
    }
    return c;
  }, [departments]);

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDir(key === "department" ? "asc" : "desc");
    }
  };

  const filtered = useMemo(() => {
    const activeFilter = FILTERS.find((f) => f.key === filter) || FILTERS[0];
    let list = departments
      .filter((d) => d.department.toLowerCase().includes(q.trim().toLowerCase()))
      .filter(activeFilter.test);

    const dir = sortDir === "asc" ? 1 : -1;
    list = [...list].sort((a, b) => {
      let av, bv;
      if (sortKey === "status") { av = STATUS_META[a.status]?.order ?? 9; bv = STATUS_META[b.status]?.order ?? 9; }
      else if (sortKey === "department") { av = a.department.toLowerCase(); bv = b.department.toLowerCase(); }
      else if (sortKey === "ready") { av = a.ready ? 1 : 0; bv = b.ready ? 1 : 0; }
      else { av = a[sortKey] ?? 0; bv = b[sortKey] ?? 0; }
      if (av < bv) return -1 * dir;
      if (av > bv) return  1 * dir;
      // secondary: critical desc, then name asc
      const c = (b.critical_open || 0) - (a.critical_open || 0);
      if (c !== 0) return c;
      return a.department.localeCompare(b.department);
    });
    return list;
  }, [departments, q, filter, sortKey, sortDir]);

  const SortIcon = ({ col }) => {
    if (sortKey !== col) return <ArrowUpDown className="w-3 h-3 opacity-30" />;
    return sortDir === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />;
  };

  return (
    <div className="mc-panel overflow-hidden" data-testid="ground-command-grid">
      {/* Header */}
      <div className="p-4 border-b border-cmdBorder flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div className="flex items-center gap-3">
          <Activity className="w-5 h-5 text-readyGreen" />
          <div>
            <div className="mc-label">Live Department Status</div>
            <h3 className="font-head font-bold text-xl uppercase mt-0.5">Ground Command Grid</h3>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2" data-testid="gcg-summary">
          <span className="flex items-center gap-1.5 text-xs border border-incidentRed/40 text-incidentRed bg-incidentRed/5 px-2 py-1">
            <Flame className="w-3 h-3" /> <span className="font-mono-live">{counts.red}</span> critical
          </span>
          <span className="flex items-center gap-1.5 text-xs border border-warnAmber/40 text-warnAmber bg-warnAmber/5 px-2 py-1">
            <Clock className="w-3 h-3" /> <span className="font-mono-live">{counts.amber}</span> attention
          </span>
          <span className="flex items-center gap-1.5 text-xs border border-readyGreen/40 text-readyGreen bg-readyGreen/5 px-2 py-1">
            <ShieldCheck className="w-3 h-3" /> <span className="font-mono-live">{counts.green}</span> cleared
          </span>
          <span className="flex items-center gap-1.5 text-xs border border-cmdBorder text-zinc-500 px-2 py-1">
            <Sparkles className="w-3 h-3" /> <span className="font-mono-live">{counts.grey}</span> idle
          </span>
        </div>
      </div>

      {/* Chip filters + search */}
      <div className="p-3 border-b border-cmdBorder flex flex-wrap items-center gap-2" data-testid="gcg-controls">
        <div className="relative min-w-[180px]">
          <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search department…"
            className="bg-black border border-cmdBorder pl-7 pr-2 py-1.5 text-sm outline-none focus:border-white/40"
            data-testid="gcg-search"
          />
        </div>
        {FILTERS.map((f) => {
          const isCritical = f.key === "critical";
          const isAttention = f.key === "attention";
          const isCertified = f.key === "certified";
          const isPending = f.key === "pending";
          const isActive = filter === f.key;
          const inactiveCls = isCritical
            ? "border-incidentRed/50 text-incidentRed hover:border-incidentRed hover:bg-incidentRed/10"
            : isAttention
              ? "border-warnAmber/50 text-warnAmber hover:border-warnAmber hover:bg-warnAmber/10"
              : isCertified
                ? "border-readyGreen/50 text-readyGreen hover:border-readyGreen hover:bg-readyGreen/10"
                : isPending
                  ? "border-orange-400/50 text-orange-400 hover:border-orange-400 hover:bg-orange-400/10"
                  : "border-cmdBorder text-zinc-400 hover:border-white";
          const activeCls = isCritical
            ? "border-incidentRed bg-incidentRed text-white"
            : isAttention
              ? "border-warnAmber bg-warnAmber text-black"
              : isCertified
                ? "border-readyGreen bg-readyGreen text-white"
                : isPending
                  ? "border-orange-400 bg-orange-400 text-black"
                  : "border-white bg-white text-black";
          return (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`text-[10px] uppercase tracking-widest font-bold px-3 py-1.5 border transition-colors ${
                isActive ? activeCls : inactiveCls
              }`}
              data-testid={`gcg-filter-${f.key}`}
            >
              {f.label}
            </button>
          );
        })}
        <div className="ml-auto font-mono-live text-xs text-zinc-500 self-center">
          {filtered.length} of {departments.length}
        </div>
      </div>

      {/* Desktop table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full mc-table">
          <thead>
            <tr>
              {COLUMNS.map((c) => (
                <th
                  key={c.key}
                  className={`${c.className || ""} ${c.align || "text-left"} ${c.sortable ? "cursor-pointer select-none hover:text-white" : ""}`}
                  onClick={() => c.sortable && toggleSort(c.key)}
                  data-testid={`gcg-th-${c.key}`}
                >
                  <span className="inline-flex items-center gap-1">
                    {c.label}
                    {c.sortable && <SortIcon col={c.key} />}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={COLUMNS.length} className="text-center text-zinc-500 py-8">No departments match this filter.</td></tr>
            )}
            {filtered.map((d) => {
              const originalIdx = departments.findIndex((x) => x.department === d.department);
              const code = `D${String((originalIdx >= 0 ? originalIdx : 0) + 1).padStart(2, "0")}`;
              const openTone = d.open_tasks === 0 ? "grey" : d.critical_open > 0 ? "red" : "amber";
              const critTone = d.critical_open > 0 ? "red" : "grey";
              const pendTone = d.pending_count > 0 ? "amber" : "grey";
              const progTone = d.in_progress_count > 0 ? "amber" : "grey";
              return (
                <tr
                  key={d.department}
                  className="hover:bg-white/5 transition-colors"
                  data-testid={`gcg-row-${d.department}`}
                >
                  <td><StatusPill status={d.status} /></td>
                  <td>
                    <div className="flex items-center gap-2">
                      <span className="font-mono-live text-[10px] text-zinc-500">{code}</span>
                      <span className="font-medium">{d.department}</span>
                    </div>
                  </td>
                  <td className="text-center" data-testid={`gcg-total-${d.department}`}><CountCell value={d.total_tasks ?? 0} tone={(d.total_tasks ?? 0) > 0 ? "grey" : "grey"} /></td>
                  <td className="text-center"><CountCell value={d.open_tasks} tone={openTone} /></td>
                  <td className="text-center" data-testid={`gcg-pending-${d.department}`}><CountCell value={d.pending_count ?? 0} tone={pendTone} /></td>
                  <td className="text-center" data-testid={`gcg-inprogress-${d.department}`}><CountCell value={d.in_progress_count ?? 0} tone={progTone} /></td>
                  <td className="text-center"><CountCell value={d.critical_open} tone={critTone} /></td>
                  <td><CertBadge ready={d.ready} submittedBy={d.submitted_by} /></td>
                  <td className="text-right">
                    <div className="inline-flex gap-2 justify-end">
                      <Link
                        to={`/pre-match/tracker?dept=${encodeURIComponent(d.department)}`}
                        className="border border-cmdBorder hover:border-white text-xs uppercase tracking-widest px-2 py-1 flex items-center gap-1 transition-colors"
                        data-testid={`gcg-goto-tracker-${d.department}`}
                        title="Open Pre-Match Tracker for this dept"
                      >
                        Tracker <ExternalLink className="w-3 h-3" />
                      </Link>
                      <Link
                        to="/readiness"
                        className="border border-cmdBorder hover:border-white text-xs uppercase tracking-widest px-2 py-1 flex items-center gap-1 transition-colors"
                        data-testid={`gcg-goto-cert-${d.department}`}
                        title="Open Readiness Certificates"
                      >
                        Cert <ExternalLink className="w-3 h-3" />
                      </Link>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile cards fallback */}
      <div className="md:hidden p-3 grid grid-cols-1 gap-3">
        {filtered.length === 0 && (
          <div className="text-center text-zinc-500 py-8 text-sm">No departments match this filter.</div>
        )}
        {filtered.map((d) => {
          const originalIdx = departments.findIndex((x) => x.department === d.department);
          const code = `D${String((originalIdx >= 0 ? originalIdx : 0) + 1).padStart(2, "0")}`;
          return <DeptCardMobile key={d.department} d={d} code={code} />;
        })}
      </div>
    </div>
  );
}
